import { FaSearch as SearchIcon } from 'react-icons/fa'
import { FiRefreshCw as RefreshIcon } from 'react-icons/fi'
import { cn } from 'utils/analytics/cn'

import { Input } from './Input'
import { TableKind, VersionView } from './TableView'

type TableManageBarProps = {
  tableView: {
    value: TableKind
    onChange: (item: TableKind) => void
  }
  hideLowTvl: {
    value: boolean
    onChange: (item: boolean) => void
  }
  hideLowVolume: {
    value: boolean
    onChange: (item: boolean) => void
  }
  searchQuery: {
    value: string
    onChange: (item: string) => void
  }
  version: VersionView
  refreshButtonProps: {
    isLoading: boolean
    isDisabled: boolean
    onClick: VoidFunction
  }
}

function Toggler({
  title,
  checked,
  onToggle,
  className,
}: {
  title: string
  checked: boolean
  onToggle: (next: boolean) => void
  className?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onToggle(!checked)}
      className={cn(
        'items-center gap-3 px-3 py-2 rounded-md border text-xs font-medium transition-colors cursor-pointer whitespace-nowrap',
        'border-kodiak-border-primary hover:border-kodiak-border-secondary',
        className
      )}
    >
      <span>{title}</span>
      <span className="w-5 h-5 rounded-sm flex items-center justify-center text-xs border-2 border-kodiak-border-primary">
        {checked ? '✓' : ''}
      </span>
    </button>
  )
}

export function TableManageBar({
  tableView,
  hideLowTvl,
  hideLowVolume,
  searchQuery,
  version,
  refreshButtonProps: { isLoading, isDisabled, onClick },
}: TableManageBarProps) {
  const VIEWS: TableKind[] = ['pools', 'tokens'] as const

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 md:flex-nowrap">
      <div className="flex justify-between w-full md:w-min flex-wrap">
        <div className="flex gap-0.5">
          {VIEWS.map((view) => (
            <button
              key={view}
              type="button"
              onClick={() => tableView.onChange(view)}
              className={cn(
                'px-3 py-2 font-medium transition-colors cursor-pointer',
                tableView.value === view ? 'text-kodiak-text-primary' : 'text-kodiak-button-disabled'
              )}
              aria-pressed={tableView.value === view}
            >
              {view[0].toUpperCase() + view.slice(1)}
            </button>
          ))}
        </div>

        <div className="flex gap-4">
          <Toggler
            title="Hide Low Volume"
            checked={hideLowVolume.value}
            onToggle={hideLowVolume.onChange}
            className="flex md:hidden"
          />

          <Toggler
            title="Hide Low TVL"
            checked={hideLowTvl.value}
            onToggle={hideLowTvl.onChange}
            className="flex md:hidden"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 w-full sm:w-auto sm:flex-1 justify-end">
        <Toggler
          title="Hide Low Volume"
          checked={hideLowVolume.value}
          onToggle={hideLowVolume.onChange}
          className="hidden md:inline-flex"
        />
        <Toggler
          title="Hide Low TVL"
          checked={hideLowTvl.value}
          onToggle={hideLowTvl.onChange}
          className="hidden md:inline-flex"
        />

        <div className="flex-1 min-w-0 sm:min-w-[200px] md:min-w-[280px] md:max-w-[280px]">
          <Input
            size="sm"
            leftIcon={<SearchIcon size={16} color="white" />}
            value={searchQuery.value}
            onChange={(e) => searchQuery.onChange(e.target.value)}
            placeholder={`Search ${version} ${tableView.value}...`}
            className="bg-transparent"
          />
        </div>

        <button
          type="button"
          onClick={onClick}
          disabled={isDisabled}
          aria-busy={isLoading}
          aria-label={`Refetch ${tableView.value}`}
          className={cn(
            'refresh-button inline-flex items-center justify-center rounded-md p-2 border border-kodiak-border-primary transition-colors shrink-0',
            isDisabled && 'opacity-60 cursor-not-allowed'
          )}
        >
          <RefreshIcon size={16} className={cn(isLoading && 'is-refreshing')} />
        </button>
      </div>
    </div>
  )
}
